import {computed, Injectable, inject, signal} from '@angular/core';
import {CookieService} from '../../_services/cookie.service';
import {CookieType} from '../../x-models/x-enums/cookie-type';
import {
  COMPOSITION_GROUPING_STORAGE_KEY,
  CompositionGroupingMode,
  parseCompositionGroupingMode
} from './composition-grouping';

/**
 * Holds the "Kombinationen einzeln" preference (Feature #534) as a signal, shared by the event-details page and
 * the single-event-analysis page — one key, one preference, so flipping the toggle on either page is reflected
 * on the other.
 *
 * Persistence goes through `CookieService.setLocalStorage(CookieType.Functional, …)`, never a bare
 * `localStorage.setItem` — the app replaces the global `setItem` with a function that THROWS specifically to
 * force every persistent write through the consent gate (see the header comment on `cookie.service.ts`).
 * Consequence worth knowing: if the visitor has not accepted functional cookies, `setLocalStorage` does NOT
 * persist the value (it removes the key instead and shows a one-time warning banner) — the signal itself still
 * updates for the current page view, but the preference will not survive a reload. That is an accepted,
 * documented degradation, not a bug: it's the same trade-off every other functional-cookie-gated preference in
 * this app makes.
 */
@Injectable({providedIn: 'root'})
export class CompositionGroupingPreferenceService {
  private readonly cookieService = inject(CookieService);

  /** The current grouping mode. Initialised from `localStorage` (default `'combined'` on any read failure). */
  readonly mode = signal<CompositionGroupingMode>(this.readInitialMode());

  /** `true` while the mode is `'individual'` — a convenience for `[class.x]`/`@if` template guards. */
  readonly individual = computed(() => this.mode() === 'individual');

  /** Sets the mode explicitly and persists it. */
  setMode(mode: CompositionGroupingMode): void {
    this.mode.set(mode);
    this.persist(mode);
  }

  /** Flips between `'combined'` and `'individual'` — what the toggle component calls on change. */
  toggle(): void {
    this.setMode(this.mode() === 'individual' ? 'combined' : 'individual');
  }

  private persist(mode: CompositionGroupingMode): void {
    this.cookieService.setLocalStorage(CookieType.Functional, COMPOSITION_GROUPING_STORAGE_KEY, mode);
  }

  private readInitialMode(): CompositionGroupingMode {
    try {
      return parseCompositionGroupingMode(localStorage.getItem(COMPOSITION_GROUPING_STORAGE_KEY));
    } catch {
      // localStorage can throw in locked-down contexts (private browsing, storage blocked, etc.) — degrade to
      // the default rather than fail component construction.
      return 'combined';
    }
  }
}
